import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PencilIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import ConfirmationModal from '../../components/ConfirmationModal';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';

interface SlaCondition {
  id: string;
  name: string;
  description?: string;
  conditionType: string;
  logicalOperator: string;
  isActive: boolean;
  createdAt: string;
  SlaConditionClause: Array<{
    id: string;
    field: string;
    operator: string;
    value: string | null;
    sortOrder: number;
  }>;
  User_SlaCondition_createdByIdToUser?: {
    id: string;
    name: string;
    email: string;
  };
}

const conditionTypeLabels: Record<string, string> = {
  START: 'Start',
  PAUSE: 'Pause',
  RESUME: 'Resume',
  RESET: 'Reset',
  CANCEL: 'Cancel',
  END: 'End'
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const CONDITION_TYPE_OPTIONS = Object.entries(conditionTypeLabels).map(([value, label]) => ({
  value,
  label,
}));

const STATUS_FILTER_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const SlaConditions: React.FC = () => {
  const navigate = useNavigate();
  const { formatDate } = useDateFormatter();
  const [searchParams, setSearchParams] = useSearchParams();

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '10', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 10;
  }, [searchParams]);

  const appliedSearch = searchParams.get('search') ?? '';
  const filterName = searchParams.get('name') ?? '';
  const filterType = searchParams.get('conditionType') ?? '';
  const filterActive = searchParams.get('active') ?? '';

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  useEffect(() => {
    setSearchInput(appliedSearch);
  }, [appliedSearch]);

  useEffect(() => {
    const id = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed === appliedSearch) return;
      patchSearchParams({ search: trimmed || null, page: '1' });
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput, appliedSearch, patchSearchParams]);

  const [conditions, setConditions] = useState<SlaCondition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; condition: SlaCondition | null }>({
    isOpen: false,
    condition: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchConditions = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {
        page: page.toString(),
        limit: pageSize.toString(),
      };
      if (filterType) params.conditionType = filterType;
      if (filterName) params.name = filterName;
      if (filterActive) params.active = filterActive;
      if (appliedSearch) params.search = appliedSearch;

      const response = await api.get('/sla-conditions', { params });
      setConditions(response.data.conditions || []);
      setTotal(response.data.total || 0);
      setError(null);
    } catch (err: unknown) {
      console.error('Error fetching SLA conditions:', err);
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to load SLA conditions');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterName, filterActive, appliedSearch, page, pageSize]);

  useEffect(() => {
    fetchConditions();
  }, [fetchConditions]);

  const handleDelete = async () => {
    if (!deleteModal.condition) return;

    try {
      setIsDeleting(true);
      await api.delete(`/sla-conditions/${deleteModal.condition.id}`);
      fetchConditions();
      setDeleteModal({ isOpen: false, condition: null });
    } catch (err: any) {
      console.error('Error deleting condition:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to delete condition';
      setError(errorMessage);
      setDeleteModal({ isOpen: false, condition: null });
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteModal = (condition: SlaCondition) => {
    setDeleteModal({ isOpen: true, condition });
  };

  const closeDeleteModal = () => {
    if (!isDeleting) {
      setDeleteModal({ isOpen: false, condition: null });
    }
  };

  const handleCreate = () => {
    navigate('/admin/sla-conditions/new');
  };

  const handleEdit = (id: string) => {
    navigate(`/admin/sla-conditions/${id}/edit`);
  };

  const getClausesSummary = (condition: SlaCondition): string => {
    if (!condition.SlaConditionClause || condition.SlaConditionClause.length === 0) {
      return 'No clauses';
    }
    const count = condition.SlaConditionClause.length;
    const operator = condition.logicalOperator;
    return `${count} clause${count !== 1 ? 's' : ''} (${operator})`;
  };

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'name':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterName}
              placeholder="Filter by name..."
              onChange={(v) => {
                patchSearchParams({ name: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'type':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={CONDITION_TYPE_OPTIONS}
              value={filterType}
              placeholder="All Types"
              onChange={(v) => {
                patchSearchParams({ conditionType: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={STATUS_FILTER_OPTIONS}
              value={filterActive}
              placeholder="All Statuses"
              onChange={(v) => {
                patchSearchParams({ active: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [filterName, filterType, filterActive, patchSearchParams]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (appliedSearch) {
      chips.push({ id: 'search', label: 'Search', value: appliedSearch, color: 'gray' });
    }
    if (filterName) {
      chips.push({ id: 'name', label: 'Name', value: filterName, color: 'blue' });
    }
    if (filterType) {
      chips.push({
        id: 'conditionType',
        label: 'Type',
        value: conditionTypeLabels[filterType] || filterType,
        color: 'teal',
      });
    }
    if (filterActive) {
      const label = STATUS_FILTER_OPTIONS.find((o) => o.value === filterActive)?.label || filterActive;
      chips.push({ id: 'active', label: 'Status', value: label, color: 'green' });
    }
    return chips;
  }, [appliedSearch, filterName, filterType, filterActive]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'name':
        patchSearchParams({ name: null, page: '1' });
        break;
      case 'conditionType':
        patchSearchParams({ conditionType: null, page: '1' });
        break;
      case 'active':
        patchSearchParams({ active: null, page: '1' });
        break;
    }
  };

  const clearAllFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      name: null,
      conditionType: null,
      active: null,
      page: '1',
    });
  };

  const totalPages = Math.ceil(total / pageSize) || 1;

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">SLA Conditions</h1>
              <p className="text-xs text-gray-500">Manage reusable condition definitions for SLA policies</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by name or description..."
                  className="pl-8 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={handleCreate}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shrink-0"
              >
                Create Condition
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {activeFilterChips.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <ActiveFilterChips
                chips={activeFilterChips}
                onRemove={handleRemoveFilterChip}
                onClearAll={clearAllFilters}
              />
            </div>
          )}

          <p className="mt-2 text-xs text-gray-600">
            Note: SLA conditions are generic and reusable across all entity types.
          </p>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-sm text-gray-600">Loading SLA conditions...</div>
          </div>
        ) : conditions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg mb-2">
              {activeFilterChips.length > 0 ? 'No SLA conditions match your filters.' : 'No SLA conditions found'}
            </p>
            <p className="text-sm">Create your first condition to get started</p>
          </div>
        ) : (
          <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('name', 'Name')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('type', 'Type')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Clauses
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('status', 'Status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {conditions.map((condition) => (
                  <tr key={condition.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{condition.name}</div>
                      {condition.description && (
                        <div className="text-sm text-gray-500 mt-1">{condition.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {conditionTypeLabels[condition.conditionType] || condition.conditionType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {getClausesSummary(condition)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {condition.isActive ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(condition.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end items-center space-x-2">
                        <button
                          onClick={() => handleEdit(condition.id)}
                          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit condition"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(condition)}
                          className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete condition"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {total > 0 && (
        <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0 mt-2 rounded-lg shadow">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label htmlFor="pageSize" className="text-sm text-gray-700">
                Show
              </label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={(e) => patchSearchParams({ pageSize: e.target.value, page: '1' })}
                className="border border-gray-300 rounded-md text-sm py-1 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-700">entries</span>
            </div>
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to{' '}
              <span className="font-medium">{Math.min(page * pageSize, total)}</span> of{' '}
              <span className="font-medium">{total}</span> entries
            </div>
          </div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            <button
              onClick={() => patchSearchParams({ page: '1' })}
              disabled={page === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              &laquo;
            </button>
            <button
              onClick={() => patchSearchParams({ page: String(Math.max(1, page - 1)) })}
              disabled={page === 1}
              className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              &lsaquo;
            </button>
            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => patchSearchParams({ page: String(Math.min(totalPages, page + 1)) })}
              disabled={page >= totalPages}
              className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              &rsaquo;
            </button>
            <button
              onClick={() => patchSearchParams({ page: String(totalPages) })}
              disabled={page >= totalPages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              &raquo;
            </button>
          </nav>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete SLA Condition"
        message={`Are you sure you want to delete the condition "${deleteModal.condition?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default SlaConditions;
